import { AfterViewInit, Component, HostListener, inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzMessageModule, NzMessageService } from 'ng-zorro-antd/message';
import { NzPageHeaderModule } from 'ng-zorro-antd/page-header';
import { NzSpaceModule } from 'ng-zorro-antd/space';
import { NzTableModule } from 'ng-zorro-antd/table';
import { Subject, takeUntil } from 'rxjs';
import { UserLoggedService } from 'src/app/core/services/userLogged.service';
import { ArticleTypeDto } from 'src/app/shared/dto/article-type.dto';
import { UserDto } from 'src/app/shared/dto/user.dto';
import { ArticleTypeService } from '../../services/article-type.service';
import { NzInputDirective } from 'ng-zorro-antd/input';
import { NzFormControlComponent, NzFormDirective, NzFormItemComponent, NzFormModule } from 'ng-zorro-antd/form';
import { NzColDirective, NzRowDirective } from 'ng-zorro-antd/grid';
import { DRAWER_WIDTH_CHANGE_PLACEMENT } from 'src/app/core/constants/components';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { DefaultComponent } from 'src/app/shared/services/view.service';
import { LoaderComponent } from 'src/app/shared/components/loader/loader.component';
import { NzButtonModule } from 'ng-zorro-antd/button';

@Component({
    selector: 'app-article-types',
    standalone: true,
    imports: [
        NzPageHeaderModule,
        NzSpaceModule,
        NzTableModule,
        NzDividerModule,
        NzDrawerModule,
        NzMessageModule,
        FormsModule,
        NzInputDirective,
        ReactiveFormsModule,
        NzFormDirective,
        NzRowDirective,
        NzFormItemComponent,
        NzColDirective,
        NzFormControlComponent,
        NzInputDirective,
        NzFormModule,
        NzIconModule,
        NzToolTipModule,
        LoaderComponent,
        NzButtonModule
    ],
    templateUrl: './article-types.component.html',
    styleUrl: './article-types.component.scss'
})
export class ArticleTypesComponent extends DefaultComponent implements OnInit, AfterViewInit, OnDestroy {

    drawerVisible: boolean = false;
    drawerTitle: string = 'Placeholder';
    fieldsReadOnly: boolean = true;
    typeSelected: ArticleTypeDto = new ArticleTypeDto();
    articleTypes: ArticleTypeDto[] = [];
    isNewType: boolean = false;
    currentUser: UserDto = new UserDto();
    isLoading: boolean = true;
    isSaving: boolean = false;
    
    innerWidth: number = 0;
    DRAWER_WIDTH_CHANGE_PLACEMENT = DRAWER_WIDTH_CHANGE_PLACEMENT;

    validateForm: FormGroup<{
        name: FormControl<string>;
    }>;

    private readonly articleTypeService = inject(ArticleTypeService);
    private readonly message = inject(NzMessageService);
    private readonly userLoggedService = inject(UserLoggedService);
    private readonly fb = inject(NonNullableFormBuilder);

    private unsubscribe$ = new Subject<void>();

    @HostListener('window:resize', ['$event'])
    onResize() {
        this.innerWidth = window.innerWidth;
    }

    constructor() {
        super('articleTypes', '');
        this.validateForm = this.fb.group({
            name: ['', [Validators.required]]
        });
    }

    async ngOnInit() {
        this.currentUser = this.userLoggedService.userLogged;
        this.innerWidth = window.innerWidth;

        await this.articleTypeService.setupWebSocket();
        this.articleTypeService.articleTypes$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: (articleTypes) => {
                    this.articleTypes = articleTypes;
                    this.typeSelected = articleTypes.find(articleType => this.typeSelected.id === articleType.id) || this.typeSelected;
                },
                error: () => {
                    this.message.error('Error cargando tipos de artículos');
                }
            });
        
        this.isLoading = false;
    }

    ngAfterViewInit() {
        this.innerWidth = window.innerWidth;
    }

    ngOnDestroy(): void {
        this.articleTypeService.disconnectWebSocket();
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    create() {
        this.fieldsReadOnly = false;
        this.typeSelected = { id: 0, name: '' };
        this.drawerTitle = 'Creando nuevo tipo de artículo';
        this.isNewType = true;
        this.validateForm.enable();
        this.openDrawer();
    }

    view(id: number) {
        this.fieldsReadOnly = true;
        this.typeSelected = this.articleTypes.find(
            (p) => p.id === id,
        ) as ArticleTypeDto;
        this.drawerTitle = 'Viendo tipo ' + this.typeSelected?.name;
        this.validateForm.disable();
        this.setData();
        this.openDrawer();
    }

    edit(id: number) {
        this.fieldsReadOnly = false;
        this.isNewType = false;
        this.typeSelected = this.articleTypes.find(
            (p) => p.id === id,
        ) as ArticleTypeDto;
        this.drawerTitle = 'Editando tipo ' + this.typeSelected?.name;
        this.validateForm.enable();
        this.setData();
        this.openDrawer();
    }

    setData() {
        // This does nothing, it's just for consistency
    }

    cancelEditing() {
        this.closeDrawer();
    }

    delete(id: number) {
        if (
            window.confirm(
                '¿Estás seguro de que quieres eliminar este proceso de tipo de artículo? Esta acción no se puede deshacer.',
            )
        ) {
            this.articleTypeService.delete(id).subscribe({
                next: (type: ArticleTypeDto) => {
                    try {
                        if (type == undefined) {
                            this.message.create('error', 'No se ha podido eliminar el tipo, tipo no encontrado');
                            throw new Error('Article type not found');
                        }
                        const index = this.articleTypes.findIndex((p) => p.id === id);
                        if (index < 0) {
                            this.message.create('error', `No se ha podido eliminar el tipo ${type.name} con id ${id}, tipo no encontrado`);
                            throw new Error('Article type not found');
                        }
                        this.articleTypes.splice(index, 1);
                        const data = [...this.articleTypes];
                        this.articleTypes = data;
                        this.message.create('success', 'Tipo de artículo eliminado correctamente');
                    } catch (error) {
                        this.message.create('error', 'Error eliminando el tipo de artículo');
                        console.error(error);
                    }
                },
                error: (error) => {
                    this.message.create('error', 'Error eliminando el tipo de artículo, error de servidor');
                    console.error(error);
                },
            });
        } else {
            console.log('Cancelando eliminación de tipo de artículo ' + id,);
        }
    }

    openDrawer() {
        this.drawerVisible = true;
    }

    closeDrawer() {
        this.drawerVisible = false;
    }

    submitForm() {
        this.isSaving = true;

        if (!this.validateForm.valid) {
            this.isSaving = false;
            this.message.error(`Porfavor, rellene los campos obligatorios`);
            return;
        }

        const customArticleTypeDto: ArticleTypeDto = {
            id: this.typeSelected.id,
            name: this.validateForm.controls.name.value ? this.validateForm.controls.name.value : ''
        };

        if (this.isNewType) {
            this.articleTypeService.save(customArticleTypeDto).subscribe({
                next: (type: ArticleTypeDto) => {
                    try {
                        if (type == undefined) {
                            this.isSaving = false;
                            throw new Error('Error creating articule type');
                        }
                        this.articleTypes.push(type);
                        const data = [...this.articleTypes];
                        this.articleTypes = data;

                        this.isSaving = false;
                        this.message.create('success', 'Tipo creado correctamente');
                        this.closeDrawer();
                    } catch (error) {
                        this.isSaving = false;
                        this.message.create('error', 'Error creando el tipo de artículo');
                        console.error('Error creating articule type', error);
                    }
                },
                error: (error) => {
                    this.isSaving = false;
                    this.message.create('error', 'Error creando el tipo de artículo, error de servidor');
                    console.error('Error creating articule type', error);
                },
            });
        } else {
            this.articleTypeService.update(customArticleTypeDto.id, customArticleTypeDto).subscribe({
                next: (type: ArticleTypeDto) => {
                    try {
                        if (type == undefined) {
                            this.isSaving = false;
                            this.message.create('error', 'No se ha podido actualizar el tipo, tipo no encontrado',);
                            throw new Error('Error updating article type');
                        }
                        const index = this.articleTypes.findIndex(
                            (r) => r.id === type.id,
                        );
                        if (index < 0) {
                            this.isSaving = false;
                            this.message.create('error', `No se ha podido actualizar el tipo ${customArticleTypeDto.name} con id ${customArticleTypeDto.id}, tipo no encontrado`,);
                            throw new Error('Article type not found');
                        }
                        this.articleTypes[index] = type;
                        const data = [...this.articleTypes];
                        this.articleTypes = data;

                        this.isSaving = false;
                        this.message.create('success', 'Tipo actualizada correctamente',);
                        this.closeDrawer();
                    } catch (error) {
                        this.isSaving = false;
                        this.message.create('error', 'Error actualizando el tipo de artículo',);
                        console.error('Error updating article type', error);
                    }
                },
                error: (error) => {
                    this.isSaving = false;
                    this.message.create('error', 'Error actualizando el tipo de artículo, error de servidor',);
                    console.error('Error updating article type', error);
                }
            });
        }
    }

}
